import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionHeader, EASE } from "./Reveal";
import { useLang } from "@/context/LanguageContext";
import { BRANDS_DATA } from "@/constants/images";

export default function Brands() {
  const { t } = useLang();
  const [open, setOpen] = useState(0);

  return (
    <section id="brands" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeader eyebrow={t.brands.eyebrow} title={t.brands.title} />

        <div className="mt-14 border-t border-white/10">
          {BRANDS_DATA.map((b, i) => {
            const tr = t.brands.items[b.id];
            const isOpen = open === i;
            return (
              <Reveal key={b.id} delay={i * 0.06}>
                <div className={`border-b border-white/10 ${isOpen ? "bg-white/[0.02]" : ""}`}>
                  <button
                    data-testid={`brand-row-${b.id}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="group flex w-full items-center gap-5 px-2 py-6 text-left sm:gap-8 sm:px-6 sm:py-8"
                  >
                    <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                    <span
                      className={`font-display text-3xl font-bold uppercase tracking-wide transition-colors sm:text-5xl ${
                        isOpen ? "text-white" : "text-slate-500 group-hover:text-slate-300"
                      }`}
                    >
                      {b.name}
                    </span>
                    <span className="ml-4 hidden rounded-full border border-steel/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-steel sm:inline-block">
                      {tr.badge}
                    </span>
                    <span
                      className="ml-auto grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 text-white transition-transform duration-300"
                      style={{ transform: isOpen ? "rotate(45deg)" : "none" }}
                    >
                      <i className="fa-solid fa-plus text-sm" />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="panel"
                        data-testid={`brand-panel-${b.id}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-8 px-2 pb-10 sm:px-6 lg:grid-cols-[440px_1fr] lg:gap-12">
                          <div className="clip-frame spotlight border border-white/10">
                            <img
                              src={b.image}
                              alt={b.name}
                              className="h-64 w-full object-cover lg:h-full"
                              data-testid={`brand-image-${b.id}`}
                            />
                          </div>
                          <div>
                            <p className="font-display text-xl font-semibold uppercase text-crimson sm:text-2xl">
                              {tr.tagline}
                            </p>
                            <p className="mt-4 leading-relaxed text-slate-300">{tr.desc}</p>

                            <div className="mt-8">
                              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                {t.brands.productsLabel}
                              </div>
                              <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                                {b.products.map((p, j) => (
                                  <li key={j} className="flex items-start gap-3 text-[15px] text-slate-200">
                                    <i className="fa-solid fa-arrow-right mt-1.5 text-xs text-crimson" />
                                    {p}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="mt-8">
                              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                {t.brands.specsLabel}
                              </div>
                              <div className="mt-4 flex flex-wrap gap-2.5">
                                {Object.entries(b.specs).map(([k, v]) => (
                                  <span
                                    key={k}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-slate-300"
                                  >
                                    <span className="text-slate-500">{k}:</span>
                                    <span className="font-medium text-steel">{v}</span>
                                  </span>
                                ))}
                              </div>
                            </div>

                            <Link
                              to={`/brands/${b.id}`}
                              data-testid={`brand-details-link-${b.id}`}
                              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-crimson transition-all hover:gap-3"
                            >
                              View full details
                              <i className="fa-solid fa-arrow-right text-xs" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
